import React from "react";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";
import {
  Users,
  Shield,
  FileText,
  FileWarning,
  MessageSquare,
  Store,
  Paperclip,
} from "lucide-react";

export type SuperAdminTab =
  | "users"
  | "domains"
  | "templates"
  | "prompt-config"
  | "parse-status"
  | "retailers"
  | "report-attachment";

interface SuperAdminSidebarProps {
  activeTab: SuperAdminTab;
  onTabChange: (tab: SuperAdminTab) => void;
  pendingUsersCount?: number;
}

export const SuperAdminSidebar: React.FC<SuperAdminSidebarProps> = ({
  activeTab,
  onTabChange,
  pendingUsersCount = 0,
}) => {
  const tabs = [
    // {
    //   id: "users" as SuperAdminTab,
    //   label: "User Management",
    //   icon: Users,
    //   description: "Approve and manage users",
    //   badge: pendingUsersCount > 0 ? pendingUsersCount : undefined,
    // },
    {
      id: "domains" as SuperAdminTab,
      label: "Domain Whitelist",
      icon: Shield,
      description: "Manage allowed email domains",
    },
    // System Config now lives in the Admin portal (AdminSidebar) — moved so the people who
    // maintain the reference data (loss factors, network tariffs, TOU rates) also own the
    // global defaults, rather than needing super-admin access for it.
    {
      id: "templates" as SuperAdminTab,
      label: "Templates",
      icon: FileText,
      description: "Manage proposal templates",
    },
    {
      id: "prompt-config" as SuperAdminTab,
      label: "Prompt Template Config",
      icon: MessageSquare,
      description: "Configure AI prompt templates",
    },
    // Sits next to Retailers because the two are read together: the retailer
    // prompt is what a parse runs, and this is whether that parse worked.
    // Moved here from the Bill Validation sidebar — the failures it lists are
    // answered by changing a prompt, which is a super-admin action.
    {
      id: "parse-status" as SuperAdminTab,
      label: "Parse Status",
      icon: FileWarning,
      description: "Invoice parse outcomes",
    },
    {
      id: "retailers" as SuperAdminTab,
      label: "Retailers",
      icon: Store,
      description: "Manage retailer parsing prompts",
    },
    {
      id: "report-attachment" as SuperAdminTab,
      label: "Report Attachment",
      icon: Paperclip,
      description: "PDF or HTML on report emails",
    },
  ];

  return (
    <div className="w-64 bg-card border-r border-border h-full">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <Shield className="h-6 w-6 text-primary" />
          <h2 className="text-lg font-semibold">Super Admin</h2>
        </div>

        <nav className="space-y-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="h-5 w-5" />
                <div className="flex-1">
                  <div className="font-medium">{tab.label}</div>
                  <div
                    className={cn(
                      "text-xs",
                      isActive
                        ? "text-primary-foreground/70"
                        : "text-muted-foreground"
                    )}
                  >
                    {tab.description}
                  </div>
                </div>
                {tab.badge && (
                  <Badge
                    variant={isActive ? "secondary" : "default"}
                    className="text-xs"
                  >
                    {tab.badge}
                  </Badge>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
